import { z } from "zod";
import { ColorSchema, HyperlinkSchema } from "./primitives";

export const InsetsSchema = z.object({ l: z.number(), t: z.number(), r: z.number(), b: z.number() });
export type Insets = z.infer<typeof InsetsSchema>;

export const SpacingSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("percent"), value: z.number() }),
  z.object({ kind: z.literal("points"), value: z.number() }),
]);
export type Spacing = z.infer<typeof SpacingSchema>;

export const RunStyleSchema = z.object({
  font: z.string(),
  fontEa: z.string().optional(),
  /** Raw size in pt. Renderer multiplies by TextBody.autofit.fontScale. */
  size: z.number(),
  bold: z.boolean().default(false),
  italic: z.boolean().default(false),
  underline: z.enum(["none", "single", "double", "heavy", "dotted", "dash", "wavy"]).default("none"),
  strike: z.enum(["none", "single", "double"]).default("none"),
  color: ColorSchema,
  highlight: ColorSchema.optional(),
  /** -0.25 subscript, +0.3 superscript, 0 normal (fraction of size) */
  baseline: z.number().default(0),
  letterSpacing: z.number().default(0),
  caps: z.enum(["none", "all", "small"]).default("none"),
  hyperlink: HyperlinkSchema.optional(),
});
export type RunStyle = z.infer<typeof RunStyleSchema>;

export const RunSchema = z.discriminatedUnion("kind", [
  RunStyleSchema.extend({ kind: z.literal("text"), text: z.string() }),
  z.object({ kind: z.literal("break") }),
  RunStyleSchema.extend({
    kind: z.literal("field"),
    type: z.enum(["slidenum", "datetime", "other"]),
    text: z.string(),
  }),
]);
export type Run = z.infer<typeof RunSchema>;

export const BulletSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("char"),
    char: z.string(),
    font: z.string(),
    color: ColorSchema.nullable(),
    size: SpacingSchema.nullable(),
  }),
  z.object({
    kind: z.literal("number"),
    scheme: z.string(),
    startAt: z.number().default(1),
    font: z.string(),
    color: ColorSchema.nullable(),
    size: SpacingSchema.nullable(),
  }),
  z.object({ kind: z.literal("image"), mediaId: z.string(), size: SpacingSchema.nullable() }),
]);
export type Bullet = z.infer<typeof BulletSchema>;

export const ParagraphSchema = z.object({
  id: z.string(),
  level: z.number().int().min(0).max(8).default(0),
  align: z.enum(["left", "center", "right", "justify", "distributed"]).default("left"),
  indent: z.number().default(0),
  marginLeft: z.number().default(0),
  marginRight: z.number().default(0),
  spaceBefore: SpacingSchema.default({ kind: "points", value: 0 }),
  spaceAfter: SpacingSchema.default({ kind: "points", value: 0 }),
  lineSpacing: SpacingSchema.default({ kind: "percent", value: 1 }),
  bullet: BulletSchema.nullable().default(null),
  rtl: z.boolean().default(false),
  runs: z.array(RunSchema),
  /** a:endParaRPr: controls empty-paragraph height */
  endRunStyle: RunStyleSchema.optional(),
});
export type Paragraph = z.infer<typeof ParagraphSchema>;

export const AutofitSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("none") }),
  z.object({ kind: z.literal("normal"), fontScale: z.number().default(1), lineSpacingReduction: z.number().default(0) }),
  z.object({ kind: z.literal("shape") }),
]);
export type Autofit = z.infer<typeof AutofitSchema>;

export const TextBodySchema = z.object({
  insets: InsetsSchema.default({ l: 7.2, t: 3.6, r: 7.2, b: 3.6 }),
  anchor: z.enum(["top", "middle", "bottom"]).default("top"),
  anchorCenter: z.boolean().default(false),
  wrap: z.boolean().default(true),
  vertical: z.enum(["horz", "vert", "vert270", "eaVert", "wordArtVert"]).default("horz"),
  autofit: AutofitSchema.default({ kind: "none" }),
  columns: z.object({ count: z.number().int(), gap: z.number() }).optional(),
  paragraphs: z.array(ParagraphSchema),
});
export type TextBody = z.infer<typeof TextBodySchema>;
